import { ASSESSOR_FUNCTION, CHANGE_CATEGORY, SCOPE_TYPE, TChangeTemplate, TPreviousChange, URGENCY } from '../models';

// Built-in mock templates. A template SUGGESTS content; nothing here is applied
// without the user reviewing it. A confideck developer can register a curated set
// via the organization mock config (organization.mock.changeV2.templates); when
// present, those replace this built-in fallback catalogue at runtime.
export const MOCK_TEMPLATES: TChangeTemplate[] = [
  {
    id: 'supplier-component-change',
    version: 5,
    name: 'Supplier and component change',
    description: 'Qualify a replacement supplier and component and assess the resulting impacts.',
    applicability: 'Use when a supplier and supplied component are being replaced or requalified.',
    featured: true,
    creationInputs: [
      {
        id: 'affectedProduct',
        label: 'Affected products',
        fieldType: 'productMultiReference',
        required: true,
        placeholder: 'Select one or more products',
        helpText: 'Used to define the request scope and downstream assessment context.',
      },
      {
        id: 'currentComponent',
        label: 'Current component',
        fieldType: 'singleChoice',
        required: true,
        placeholder: 'Select the current component',
        options: [
          'Sensor Patch SP-100',
          'Signal Acquisition Module SAM-40',
          'Patient Cable PC-20',
          'Monitoring Display Unit MDU-5',
        ],
      },
      {
        id: 'newComponent',
        label: 'New component',
        fieldType: 'shortText',
        required: true,
        placeholder: 'Enter the new component name or number',
      },
      {
        id: 'currentSupplier',
        label: 'Current supplier',
        fieldType: 'singleChoice',
        required: true,
        placeholder: 'Select the current supplier',
        options: ['Medisense Components', 'Alpine Sensor Systems', 'Helix Medical Materials', 'VitaCore Electronics'],
      },
      {
        id: 'newSupplier',
        label: 'New supplier',
        fieldType: 'shortText',
        required: true,
        placeholder: 'Enter the new supplier',
      },
      {
        id: 'changeReason',
        label: 'Reason for change',
        fieldType: 'longText',
        required: true,
        placeholder: 'Explain why the supplier or component transition is needed',
        helpText: 'This will also prefill the assessment context.',
      },
    ],
    requestPrefills: {
      title: 'Supplier and component change for {{input:currentComponent}}',
      description:
        'Affected products: {{input:affectedProduct}}. This change replaces {{input:currentComponent}}, currently supplied by {{input:currentSupplier}}, with {{input:newComponent}} from {{input:newSupplier}}. The proposed transition includes supplier qualification, component and material equivalence, incoming-control review, risk-management updates, verification and validation, controlled-document updates, and regulatory and UDI impact assessment, as applicable. Reason for change: {{input:changeReason}}.',
      reasonOrTrigger:
        '{{input:changeReason}}. The change is required to transition {{input:affectedProduct}} from {{input:currentSupplier}} / {{input:currentComponent}} to {{input:newSupplier}} / {{input:newComponent}} while maintaining controlled supply and traceability.',
      desiredOutcome:
        'Approve {{input:newSupplier}} and {{input:newComponent}} for {{input:affectedProduct}} as qualified replacements for {{input:currentSupplier}} and {{input:currentComponent}}, supported by documented evidence that safety, performance, usability, intended purpose, regulatory conformity and supply continuity are maintained.',
      implementationProposal:
        'Complete supplier qualification and the quality agreement; compare specifications, materials and interfaces for {{input:newComponent}} against {{input:currentComponent}}; update the bill of materials, component specifications, supplier controls, risk-management file and applicable technical documentation for {{input:affectedProduct}}; execute required verification and validation; document the regulatory and UDI determination; and introduce the new component through an approved, traceable cutover.',
      scopeType: SCOPE_TYPE.QMS_AND_TD,
      urgency: URGENCY.NORMAL,
      changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    },
    assessmentSections: [
      'Supplier qualification',
      'Incoming inspection impact',
      'Material equivalence',
      'Risk management impact',
    ],
    suggestedRoles: [ASSESSOR_FUNCTION.SUPPLIER_QUALITY, ASSESSOR_FUNCTION.ENGINEERING, ASSESSOR_FUNCTION.PRODUCTION],
    suggestedTasks: [
      { title: 'Qualify {{input:newSupplier}}', assignedFunction: ASSESSOR_FUNCTION.SUPPLIER_QUALITY },
      {
        title:
          'Assess {{input:newComponent}} against {{input:currentComponent}} and update the Device Description and Specification for {{input:affectedProduct}}',
        assignedFunction: ASSESSOR_FUNCTION.ENGINEERING,
        documents: [
          {
            _id: '6a4a475a1f350133d5fb33af',
            name: 'Device Description & Specification',
          },
        ],
      },
      {
        title: 'Plan the cutover from {{input:currentSupplier}} to {{input:newSupplier}}',
        assignedFunction: ASSESSOR_FUNCTION.PRODUCTION,
      },
    ],
    suggestedAffectedObjectCategories: [
      'Supplier records',
      'Specifications',
      'Incoming inspection',
      'Approved supplier list',
      'Quality agreements',
      'Bills of material',
      'Risk management file',
    ],
    suggestedWorkflowPolicy: 'Standard route with supplier qualification before assessment approval.',
  },
  {
    id: 't-standards-update',
    name: 'Standards update',
    description: 'A standard or regulation revision that may require documentation updates.',
    applicability: 'Use when a harmonized standard or regulation is revised.',
    requestPrefills: {
      reasonOrTrigger: 'A new revision of an applicable standard or regulation was published.',
      desiredOutcome: 'Documentation demonstrates continued conformity to the revised requirements.',
      scopeType: SCOPE_TYPE.QMS,
      urgency: URGENCY.NORMAL,
      changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    },
    assessmentSections: ['Gap analysis against revision', 'Standards conformity', 'Documentation impact'],
    suggestedRoles: [ASSESSOR_FUNCTION.REGULATORY, ASSESSOR_FUNCTION.QUALITY_ASSURANCE, ASSESSOR_FUNCTION.CLINICAL],
    suggestedTasks: [
      { title: 'Perform gap analysis against the new revision', assignedFunction: ASSESSOR_FUNCTION.REGULATORY },
      { title: 'Identify documents needing content changes', assignedFunction: ASSESSOR_FUNCTION.QUALITY_ASSURANCE },
    ],
    suggestedAffectedObjectCategories: ['Standards list', 'GSPR checklist', 'Technical documentation'],
    suggestedWorkflowPolicy: 'Standard route with a regulatory gap analysis before assessment approval.',
  },
  {
    id: 't-qms-document-update',
    name: 'QMS document update',
    description: 'A controlled QMS document needs to be revised.',
    applicability: 'Use for process, procedure or work-instruction updates that do not change a product.',
    requestPrefills: {
      reasonOrTrigger: 'A QMS process or procedure requires revision.',
      desiredOutcome: 'The controlled document reflects the intended process.',
      scopeType: SCOPE_TYPE.QMS,
      urgency: URGENCY.LOW,
      changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    },
    assessmentSections: ['Process impact', 'Training impact', 'Interfacing documents'],
    suggestedRoles: [ASSESSOR_FUNCTION.QUALITY_ASSURANCE, ASSESSOR_FUNCTION.TRAINING],
    suggestedTasks: [
      { title: 'Revise the controlled document', assignedFunction: ASSESSOR_FUNCTION.QUALITY_ASSURANCE },
      { title: 'Plan training on the revised process', assignedFunction: ASSESSOR_FUNCTION.TRAINING },
    ],
    suggestedAffectedObjectCategories: ['Procedures', 'Work instructions', 'Training records'],
    suggestedWorkflowPolicy: 'Lightweight route with a training step where roles are affected.',
  },
  {
    id: 't-product-software-change',
    name: 'Product software change',
    description: 'A change to medical device software.',
    applicability: 'Use for software modifications, including bug fixes and feature changes.',
    requestPrefills: {
      reasonOrTrigger: 'A software modification is required.',
      desiredOutcome: 'Verified software that maintains safety and performance.',
      scopeType: SCOPE_TYPE.PRODUCT,
      urgency: URGENCY.NORMAL,
      changeCategory: CHANGE_CATEGORY.MAINTENANCE,
    },
    assessmentSections: ['Software risk analysis', 'Verification scope', 'Cybersecurity impact', 'Usability impact'],
    suggestedRoles: [ASSESSOR_FUNCTION.SOFTWARE, ASSESSOR_FUNCTION.ENGINEERING, ASSESSOR_FUNCTION.REGULATORY],
    suggestedTasks: [
      { title: 'Update software risk analysis', assignedFunction: ASSESSOR_FUNCTION.SOFTWARE },
      { title: 'Define verification scope', assignedFunction: ASSESSOR_FUNCTION.ENGINEERING },
    ],
    suggestedAffectedObjectCategories: ['Software items', 'Verification protocols', 'Cybersecurity file'],
    suggestedWorkflowPolicy: 'Standard route with software verification before assessment approval.',
  },
];

// Previous changes offered for reuse. Reuse copies STRUCTURE, role suggestions
// and task definitions only. It never copies assessment answers, approvals,
// signatures, evidence, completion dates or effectiveness conclusions.
export const MOCK_PREVIOUS_CHANGES: TPreviousChange[] = [
  {
    id: 'pc-2025-0188',
    displayId: 'CHG-2025-0188',
    title: 'Alternative titanium supplier for Cardionyx',
    summary: 'Qualified a second titanium supplier after a discontinuation notice.',
    scopeType: SCOPE_TYPE.PRODUCT,
    suggestedRoles: [ASSESSOR_FUNCTION.SUPPLIER_QUALITY, ASSESSOR_FUNCTION.ENGINEERING, ASSESSOR_FUNCTION.PRODUCTION],
    suggestedTasks: [
      { title: 'Qualify alternative supplier', assignedFunction: ASSESSOR_FUNCTION.SUPPLIER_QUALITY },
      { title: 'Assess material equivalence', assignedFunction: ASSESSOR_FUNCTION.ENGINEERING },
    ],
    reusableStructure: [
      'Supplier qualification section',
      'Material equivalence section',
      'First-article inspection task',
    ],
  },
  {
    id: 'pc-2025-0143',
    displayId: 'CHG-2025-0143',
    title: 'IFU update for revised warnings',
    summary: 'Revised instructions for use to reflect updated warnings.',
    scopeType: SCOPE_TYPE.QMS,
    suggestedRoles: [ASSESSOR_FUNCTION.REGULATORY, ASSESSOR_FUNCTION.QUALITY_ASSURANCE, ASSESSOR_FUNCTION.TRAINING],
    suggestedTasks: [
      { title: 'Revise labeling and IFU', assignedFunction: ASSESSOR_FUNCTION.REGULATORY },
      { title: 'Plan distribution of the revised IFU', assignedFunction: ASSESSOR_FUNCTION.TRAINING },
    ],
    reusableStructure: ['Labeling impact section', 'Distribution task'],
  },
  {
    id: 'pc-2025-0121',
    displayId: 'CHG-2025-0121',
    title: 'CerebroTriage firmware maintenance release',
    summary: 'Maintenance firmware release addressing minor defects.',
    scopeType: SCOPE_TYPE.PRODUCT,
    suggestedRoles: [ASSESSOR_FUNCTION.SOFTWARE, ASSESSOR_FUNCTION.ENGINEERING],
    suggestedTasks: [
      { title: 'Update software risk analysis', assignedFunction: ASSESSOR_FUNCTION.SOFTWARE },
      { title: 'Define verification scope', assignedFunction: ASSESSOR_FUNCTION.ENGINEERING },
    ],
    reusableStructure: ['Software risk analysis section', 'Verification scope section'],
  },
];

export const getTemplateById = (templates: TChangeTemplate[], id?: string): TChangeTemplate | undefined =>
  id ? templates.find((template) => template.id === id) : undefined;

export const getPreviousChangeById = (id?: string): TPreviousChange | undefined =>
  id ? MOCK_PREVIOUS_CHANGES.find((change) => change.id === id) : undefined;
